import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { SearchableSelect, SearchableSelectOption } from "@/components/searchable_select"
import { ActiveFilterIcon } from "@/global_filter/active_filter_icon"
import { useAsyncEffect } from "@/utils"
import { useState } from "preact/compat"
import { distributor_valid_for_choices, GlobalFilterState } from "@/global_filter"
import { CustomerRef, customer_choices_with_selection, fetch_customer_refs } from "./customer_labels"
import { UserState } from "@/auth/user.tsx"
import { Button } from "@/components/ui/button"
import { ChevronDown } from "lucide-react"

export const ALL_DISTRIBUTORS_OPTION = "All Customers"

/**
 * Whether a customer picker lists its options or searches them: past 11 it searches. Shared by the
 * global customer filter and the cash application page's customer column, so both behave alike.
 */
export function use_searchable_choices(option_count: number): boolean {
  return option_count > 11
}

export function CustomerFilter() {
  const [choices, setChoices] = useState<CustomerRef[]>([])
  const [all_time, set_all_time] = useState<CustomerRef[]>([])
  const distributor = GlobalFilterState.use(s => s.distributor)
  const [start_date, end_date, date_kind] = GlobalFilterState.use(s => [s.startDate, s.endDate, s.dateKind])
  const org_id = UserState.use(u => u?.org_id ?? null)

  function handleDistributorChange(distributor: string) {
    if (distributor === ALL_DISTRIBUTORS_OPTION) {
      GlobalFilterState.set(s => ({ ...s, distributor: null }))
    } else {
      GlobalFilterState.set(s => ({ ...s, distributor }))
    }
  }

  // Every customer the org has had: a selection outside it (another org's, a stale saved one) is
  // dropped. A selection merely outside the date window is kept — see below.
  useAsyncEffect(async () => {
    const res = await fetch_customer_refs()
    set_all_time(res)
    if (!distributor_valid_for_choices(distributor, res)) {
      GlobalFilterState.set(s => ({ ...s, distributor: null }))
    }
  }, [org_id])

  // The customers with rows in the page's window, with each name inside Other listed on its own.
  useAsyncEffect(async () => {
    const res = await fetch_customer_refs({ start_date, end_date, date_kind })
    setChoices([{ name: ALL_DISTRIBUTORS_OPTION, value: ALL_DISTRIBUTORS_OPTION }, ...res])
  }, [org_id, start_date?.getTime(), end_date?.getTime(), date_kind])

  let current_value = distributor ?? ALL_DISTRIBUTORS_OPTION
  let active = current_value !== ALL_DISTRIBUTORS_OPTION
  const shown = customer_choices_with_selection(choices, distributor, all_time)
  const selectedChoice = shown.find(c => c.value === current_value)
  const displayText = selectedChoice?.name ?? ALL_DISTRIBUTORS_OPTION
  const useAutocomplete = use_searchable_choices(shown.length)

  const searchableOptions: SearchableSelectOption[] = shown.map(choice => ({
    value: choice.value,
    label: choice.name,
  }))

  if (useAutocomplete) {
    return (
      <div className="font-medium">
        <SearchableSelect
          options={searchableOptions}
          value={current_value}
          onValueChange={handleDistributorChange}
          searchPlaceholder="Search distributors..."
          emptyMessage="No distributors found."
          renderTrigger={(selectedOption, isOpen) => (
            <Button
              variant="outline"
              className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-plue-100 placeholder:text-muted-foreground focus:outline-hidden focus:ring-1 focus:ring-plue-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 font-medium">
              <span className="line-clamp-1">{displayText}</span>
              <div className="flex items-center gap-1">
                {active && <ActiveFilterIcon />}
                <ChevronDown className="h-4 w-4 opacity-50" />
              </div>
            </Button>
          )}
        />
      </div>
    )
  }

  return (
    <div className="font-medium">
      <Select value={current_value} onValueChange={handleDistributorChange}>
        <SelectTrigger className="w-full ">
          <SelectValue placeholder={distributor ?? ALL_DISTRIBUTORS_OPTION} />
          {active && <ActiveFilterIcon />}
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {shown.map((item, idx) => (
              <SelectItem key={idx} value={item.value}>
                <div className="flex items-center">
                  <span>{item.name}</span>
                </div>
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  )
}
